import { Injectable } from '@angular/core';
import { Confirmation, ConfirmationService, MessageService } from 'primeng/api';
import { Subject, filter, map } from 'rxjs';
import { AlertServiceEvent, AlertServiceEventType } from 'src/app/interface/alert.interface';

type AlertType = 'success' | 'error' | 'warning' | 'info';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private _events = new Subject<AlertServiceEvent>();

  constructor(
    private _message: MessageService,
    private _confirm: ConfirmationService,
  ) { }

  /** ดักจับการทำงานของโหลดหน้าเว็บทั้งหมด */
  get onLoadingPage() {
    return this._events
      .pipe(filter(res => res.type == AlertServiceEventType.LoadingPage))
      .pipe(map(res => res.data as boolean));
  }

  /** ดักจับการทำงานของ alert แจ้งเตือนทั้งหมด */
  get onNotifications() {
    return this._events
      .pipe(filter(res => res.type != AlertServiceEventType.LoadingPage))
      .pipe(map(res => res.data));
  }

  /** แจ้งเตือนแบบง่ายๆ */
  notify(type: AlertType, message: string) {
    const icon: { [key: string]: string } = { 'success': 'check_circle', 'error': 'cancel', 'info': 'info', 'warning': 'warning' };
    const severity: { [key: string]: string } = { 'success': 'success', 'error': 'error', 'info': 'info', 'warning': 'warn' };
    this._message.clear();
    this._message.add({
      key: 'notify',
      icon: icon[type] || 'help',
      severity: severity[type] || 'info',
      summary: message,
    });
    this._events.next({ type: AlertServiceEventType.Notify, data: { type, message } });
  }

  /** ยืนยันการทำรายการ */
  async confirm(message: string, options?: Confirmation) {
    const eventEmiiter = (type: AlertServiceEventType, data: any) => {
      if (!options) return;
      if (options.rejectButtonStyleClass == 'd-none') return;
      this._events.next({ type, data });
    };
    return new Promise<boolean>(resolve => {
      if (!options) options = {};
      options.key = options.key || 'dialog-confirm';
      options.message = message;
      options.icon = options.icon || 'pi pi-info-circle';
      options.acceptIcon = options.acceptIcon || 'pi';
      options.rejectIcon = options.rejectIcon || 'pi';
      options.acceptLabel = options.acceptLabel || 'ยืนยัน';
      options.rejectLabel = options.rejectLabel || 'ยกเลิก';
      options.accept = () => resolve(true);
      options.reject = () => resolve(false);
      eventEmiiter(AlertServiceEventType.ConfirmRequest, { message, options });
      this._confirm.confirm(options);
    }).then(status => {
      eventEmiiter(AlertServiceEventType.ConfirmResponse, status);
      return status;
    });
  }

  /** แจ้งเตือนการทำรายการ */
  async dialog(message: string, options?: Confirmation) {
    if (!options) options = {};
    options.rejectButtonStyleClass = 'd-none';
    options.acceptLabel = options.acceptLabel || 'ตกลง';
    this._events.next({ type: AlertServiceEventType.DialogRequest, data: { message, options } });
    return this.confirm(message, options).then(status => {
      this._events.next({ type: AlertServiceEventType.DialogResponse, data: { message, options } });
      return status;
    });
  }

  /** โหลดหน้าเว็บทั้งหมด */
  loadingPage(status: boolean) { this._events.next({ type: AlertServiceEventType.LoadingPage, data: status }); }
}
